import axios from "axios";
import { DataGrid } from "devextreme-react";
import {
  Column,
  Item,
  Scrolling,
  SearchPanel,
  Selection,
  Toolbar,
} from "devextreme-react/data-grid";
import Switch from "devextreme-react/switch";
import { alert } from "devextreme/ui/dialog";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { notifyMsg } from "../../common/notify";
import { resp as respData } from "./data";
import { hasPermission } from "../../../utils/hasPermission";
import { PERM, REPORT_EDIT_ALL_DIVISIONS } from "../../../utils/permissions";

const BASE = process.env.REACT_APP_BECCO_API_URL;
const REPORT_PERMISSION_MESSAGE = "전체 프로젝트 입력 권한은 OF, SRD, CEO 부서에만 부여할 수 있습니다.";

const showReportPermissionAlert = (event) => {
  event.preventDefault();
  event.stopPropagation();
  if (!event.repeat) void alert(REPORT_PERMISSION_MESSAGE, "권한 설정");
};

const getRespName = (id) => respData.find((r) => r.id === id)?.name ?? id ?? "-";

function PermissionSwitch({ row, div, isAdmin, pending, onToggle }) {
  const isGrantBlocked = isAdmin && !row.allowed && row.key === PERM.REPORT_EDIT_ALL &&
    !REPORT_EDIT_ALL_DIVISIONS.includes(div);

  if (isGrantBlocked) {
    return (
      <div style={{ display: "inline-block", position: "relative", verticalAlign: "middle" }}>
        <div aria-hidden="true" style={{ pointerEvents: "none" }}>
          <Switch value={false} readOnly focusStateEnabled={false} tabIndex={-1} />
        </div>
        <button type="button" aria-label="전체 프로젝트 입력 권한 제한 안내"
          style={{
            position: "absolute", top: 0, left: 0, zIndex: 1,
            width: "100%", height: "100%",
            padding: 0, border: 0, background: "transparent", cursor: "pointer",
          }}
          onClick={showReportPermissionAlert}
          onKeyDownCapture={(event) => {
            if (event.key === "Enter" || event.key === " ") showReportPermissionAlert(event);
          }} />
      </div>
    );
  }

  return (
    <Switch value={!!row.allowed} disabled={!isAdmin || pending}
      onValueChanged={({ value, event, component }) => event && onToggle(row, value, component)} />
  );
}

// 프로필이 바뀌면 새로 마운트해 이전 조회/저장 응답이 현재 화면에 섞이지 않게 한다.
function ProfilePermissions({ selectedProfile, isAdmin }) {
  const [permissionRows, setPermissionRows] = useState([]);
  const [pendingFeatures, setPendingFeatures] = useState(new Set());
  const pendingRef = useRef(new Set());
  const activeRef = useRef(false);

  useEffect(() => {
    activeRef.current = true;
    axios.get(`${BASE}/api/permission/profiles/${selectedProfile.id}/permissions`)
      .then((res) => {
        if (activeRef.current) setPermissionRows(res.data);
      })
      .catch(() => {
        if (activeRef.current) void alert("권한 목록을 불러오지 못했습니다. 다시 선택해 주세요.", "권한 설정");
      });
    return () => { activeRef.current = false; };
  }, [selectedProfile.id]);

  const onToggle = async (row, newValue, component) => {
    if (!activeRef.current || !isAdmin || pendingRef.current.has(row.feature_id)) return;
    const restoreValue = () => component?.option("value", !!row.allowed);
    if (newValue && row.key === PERM.REPORT_EDIT_ALL &&
        !REPORT_EDIT_ALL_DIVISIONS.includes(selectedProfile.div)) {
      restoreValue();
      await alert(REPORT_PERMISSION_MESSAGE, "권한 설정");
      return;
    }

    pendingRef.current.add(row.feature_id);
    setPendingFeatures(new Set(pendingRef.current));
    try {
      const response = await axios.put(
        `${BASE}/api/permission/profiles/${selectedProfile.id}/features/${row.feature_id}`,
        { allowed: newValue ? 1 : 0 }
      );
      // 인증/권한 인터셉터가 응답 없이 종료한 경우도 성공으로 표시하지 않는다.
      if (!response) throw new Error("Permission update failed");
      if (!activeRef.current) return;
      setPermissionRows((prev) => prev.map((r) =>
        r.feature_id === row.feature_id ? { ...r, allowed: newValue ? 1 : 0 } : r
      ));
      notifyMsg(
        `${row.page}${row.tab ? ` > ${row.tab}` : ""} > ${row.feature} 권한이 ${newValue ? "추가" : "제거"}되었습니다. 로그아웃 후 재로그인 시 적용됩니다.`,
        "success"
      );
    } catch (error) {
      if (!activeRef.current) return;
      restoreValue();
      void alert(error.response?.data?.message || "권한을 변경하지 못했습니다. 다시 시도해 주세요.", "권한 설정");
    } finally {
      pendingRef.current.delete(row.feature_id);
      if (activeRef.current) setPendingFeatures(new Set(pendingRef.current));
    }
  };

  return (
    <DataGrid
      dataSource={permissionRows}
      keyExpr="feature_id"
      height="100%"
      showBorders
      hoverStateEnabled
    >
      <Scrolling mode="virtual" showScrollbar="always" />
      <SearchPanel visible={true} />
      <Toolbar>
        <Item location="before">
          <span style={{ fontWeight: 500, color: "#009688", marginLeft: 10 }}>
            {`${getRespName(selectedProfile.resp)} · ${selectedProfile.canBeAMgr ? "Mgr" : "Non-Mgr"} · ${selectedProfile.div ?? "-"} · ${selectedProfile.jd ?? "-"}`}
          </span>
        </Item>
        <Item location="after" name="searchPanel" />
      </Toolbar>
      <Column dataField="page" caption="Page" width={160} />
      <Column dataField="tab" caption="Tab" width={200} />
      <Column dataField="feature" caption="Feature" />
      <Column
        dataField="allowed"
        caption="허용"
        width={80}
        alignment="center"
        cellRender={({ data }) => (
          <PermissionSwitch row={data} div={selectedProfile.div} isAdmin={isAdmin}
            pending={pendingFeatures.has(data.feature_id)} onToggle={onToggle} />
        )}
      />
    </DataGrid>
  );
}

export default function PermissionMatrix() {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const userInfo = useSelector((state) => state.userInfo);
  const isAdmin = hasPermission(userInfo.permissions, PERM.ORG_PERMISSION);

  useEffect(() => {
    axios.get(`${BASE}/api/permission/profiles`).then((res) => {
      setProfiles(res.data);
    });
  }, []);

  const onSelectionChanged = ({ selectedRowsData }) => {
    setSelectedProfile(selectedRowsData[0] ?? null);
  };

  return (
    <div
      style={{
        margin: "20px auto",
        width: "95vw",
        display: "flex",
        gap: 16,
        height: "calc(100vh - 180px)",
      }}
    >
      <div style={{ width: 380, flexShrink: 0 }}>
        <DataGrid
          dataSource={profiles}
          keyExpr="id"
          height="100%"
          showBorders
          hoverStateEnabled
          onSelectionChanged={onSelectionChanged}
        >
          <Selection mode="single" />
          <Scrolling mode="virtual" showScrollbar="always" />
          <Toolbar>
            <Item location="before">
              <span style={{ fontWeight: 500, color: "#009688", marginLeft: 10 }}>
                PROFILE LIST
              </span>
            </Item>
          </Toolbar>
          <Column
            caption="Resp"
            dataField="resp"
            width={80}
            calculateDisplayValue={(row) => getRespName(row.resp)}
          />
          <Column
            caption="Mgr"
            dataField="canBeAMgr"
            width={50}
            alignment="center"
            calculateDisplayValue={(row) => (row.canBeAMgr ? "O" : "-")}
          />
          <Column caption="Division" dataField="div" width={70} />
          <Column caption="Job Description" dataField="jd" />
        </DataGrid>
      </div>

      <div style={{ flex: 1 }}>
        {selectedProfile ? (
          <ProfilePermissions key={selectedProfile.id} selectedProfile={selectedProfile} isAdmin={isAdmin} />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#aaa",
              fontSize: 14,
            }}
          >
            좌측에서 프로파일을 선택하세요
          </div>
        )}
      </div>
    </div>
  );
}
